import { useRouter } from 'next/router';
import { useContext, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useInteraction } from '@/common/hooks/useInteraction';
import { UnsavedChangesContext } from '@/components/UnsavedChanges';
import { useAppFlags } from '$launch-darkly/app-flags';
import { CreateOfferSkeleton } from './components/CreateOfferSkeleton';
import { FormContainer } from './components/FormContainer';
import { CreateOfferProvider } from './context/CreateOfferProvider';
import { BuyerMethod } from './types';

const BUYER_TYPE_QUERY_PARAM_TO_METHOD: Record<string, BuyerMethod> = {
  existing: BuyerMethod.existingBuyer,
  new: BuyerMethod.newBuyer,
  unrepresented: BuyerMethod.unrepresentedBuyer,
};

interface Props {
  sourceId?: string | undefined;
  buyerId?: string | undefined;
  listingId?: string;
  buyerType?: string;
  interactionId?: string;
  incomplete?: boolean;
}

interface FormProps {
  sourceId?: string;
  buyerId?: string;
  listingId?: string;
  buyerMethod?: BuyerMethod;
}

const CreateOfferForm = ({
  sourceId,
  buyerId,
  listingId,
  buyerMethod,
}: FormProps) => {
  const { setIsPageDirty } = useContext(UnsavedChangesContext);
  const methods = useForm({
    mode: 'all',
    defaultValues: {
      buyerMethod: buyerMethod ?? BuyerMethod.existingBuyer,
      listingId,
      buyerAgents: [{}],
      newBuyerClients: [
        {
          firstName: undefined,
          lastName: undefined,
          email: undefined,
          entityType: '',
        },
      ],
    },
  });

  const {
    formState: { isDirty },
  } = methods;

  useEffect(() => {
    setIsPageDirty(isDirty);
  }, [isDirty, setIsPageDirty]);

  return (
    <CreateOfferProvider
      sourceId={sourceId}
      buyerId={buyerId}
      openedFromListing={!!listingId}
    >
      <FormProvider {...methods}>
        <FormContainer />
      </FormProvider>
    </CreateOfferProvider>
  );
};

export const CreateOffer = ({
  sourceId,
  buyerId,
  listingId = '',
  buyerType = '',
}: Props) => {
  const { isReady } = useRouter();
  const { txmLinkOffersListings, __loading__: flagsLoading } = useAppFlags();
  const { data: listing, isLoading: listingLoading } = useInteraction(
    listingId,
    'listing',
    !txmLinkOffersListings,
    {
      shouldRetryOnError: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );

  if (!isReady || flagsLoading || listingLoading) {
    return <CreateOfferSkeleton />;
  }

  const validListingId = listing ? listingId : undefined;
  const buyerMethod = txmLinkOffersListings
    ? BUYER_TYPE_QUERY_PARAM_TO_METHOD[buyerType]
    : undefined;

  return (
    <CreateOfferForm
      sourceId={sourceId}
      buyerId={buyerId}
      listingId={validListingId}
      buyerMethod={buyerMethod}
    />
  );
};
